/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { CaseService } from 'app/cm/services/case-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { SaveReasonForReferralCustomElement } from 'app/cm/admin/referral/save-reason-for-referral';
import { deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { Toaster } from 'app/shared/lib/toaster';
import { confirmation } from 'app/shared/elements/ess-confirm';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class ReasonsForReferral extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected referralReasons: ReadonlyArray<CM.ReferralReason> = [];

    private sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;
    protected includeInactive = false;
    protected isValid: boolean = false;
    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $saveReasonForReferral!: SaveReasonForReferralCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: CaseService,
        private readonly toaster: Toaster,
        ...rest: any[]
    ) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'description',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    private editReasonForReferral(referralReason: CM.ReferralReason): void {
        if(!referralReason || !this.$modal) {
            return;
        }

        this.$saveReasonForReferral.referralReason = deepClone(referralReason);
        this.$modal.open();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadReasonsForReferral();
    }

    protected addReferralReason() {
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$saveReasonForReferral.reset();
        this.$modal.close();
    }

    protected async handleSaveReasonForReferralFormSuccess(): Promise<void> {
        if(this.$saveReasonForReferral.reset) {
            this.$saveReasonForReferral.reset();
        }
        this.$modal.close();
    }

    public async submit(): Promise<boolean> {
        const response = await this.$saveReasonForReferral.saveReferralReason();
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
        this.reload();
        return response;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadReasonsForReferral(): Promise<void> {
        const includes: string[] = [];
        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadReferralReasons(options, includes);
            this.referralReasons = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateReferralReason(referralReason: CM.ReferralReason, isActive: boolean) {
        if(!isActive) {
            const result = await confirmation.showDeactivate('cm:referral.reason.$long');
            if(result.dialogResponse === confirmation.options.CANCEL) { return; }
        }

        try {
            this.state.beginSave();
            const response = await this.service.activateReferralReason(referralReason.id!, isActive);

            if(response.isSuccessful) {
                this.loadReasonsForReferral();
            }

        } catch(error) {
            this.toaster.standard.activate('cm:referral.reason.$long', isActive, false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }


}
